import { useState, useEffect } from "react";
import { Contact } from "@/types/contacts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { areNamesEquivalent } from "@/utils/entityNormalizer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Mail, Phone, MapPin, AlertTriangle, Building2 } from "lucide-react";
import { DeleteRowDialog } from "@/components/DeleteRowDialog";
import { EditContactDialog } from "@/components/EditContactDialog";
import { useUserRole } from "@/hooks/useUserRole";
import { recordImportMetadata } from "@/utils/importMetadata";
import { useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";

interface ContactTableProps {
  contacts: Contact[];
  onUpdateContact?: (contact: Contact) => void;
  onDeleteContact?: (contact: Contact) => Promise<void>;
}

export function ContactTable({ contacts, onUpdateContact, onDeleteContact }: ContactTableProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [showOrphansOnly, setShowOrphansOnly] = useState(false);
  const [orphanContactIds, setOrphanContactIds] = useState<Set<number>>(new Set());
  const { isAdmin, isEditor, isCliente, isRsvp } = useUserRole();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const canEdit = isAdmin || isEditor || isCliente || isRsvp;

  useEffect(() => {
    checkOrphanContacts();
  }, [contacts]);

  const checkOrphanContacts = async () => {
    try {
      const { data: entities, error } = await supabase
        .from("entities")
        .select("name");

      if (error) throw error;

      const orphanIds = new Set<number>();
      
      for (const contact of contacts) {
        const hasMatch = entities?.some(e => areNamesEquivalent(e.name, contact.company));
        if (!hasMatch) {
          orphanIds.add(contact.id);
        }
      }

      setOrphanContactIds(orphanIds);
    } catch (error) {
      console.error("Error checking orphan contacts:", error);
    }
  };

  const isOrphan = (contactId: number) => orphanContactIds.has(contactId);

  const filteredContacts = showOrphansOnly 
    ? contacts.filter(c => isOrphan(c.id))
    : contacts;

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setEditDialogOpen(true);
  };

  const handleUpdate = async (contact: Contact) => {
    if (onUpdateContact) {
      onUpdateContact(contact);
      
      await recordImportMetadata({
        tableName: 'contacts',
        entityType: contact.entityType,
        updateType: 'manual_edit',
        recordsAffected: 1
      });
      queryClient.invalidateQueries({ queryKey: ['lastUpdate', 'contacts'] });
    }
  };

  const handleDeleteClick = (contact: Contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete || !onDeleteContact) return;
    
    try {
      await onDeleteContact(contactToDelete);
      
      await recordImportMetadata({
        tableName: 'contacts',
        entityType: contactToDelete.entityType,
        updateType: 'manual_edit',
        recordsAffected: 1
      });
      
      queryClient.invalidateQueries({ queryKey: ['lastUpdate', 'contacts'] });
      
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const getTypeLabel = (type: string, source?: string) => {
    if (source === 'form') {
      return 'RSVP';
    }
    
    const labels = {
      restaurant: "Restaurante",
      operator: "Operadora",
      consolidator: "Consolidadora",
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getTypeColor = (type: string, source?: string) => {
    if (source === 'form') {
      return 'bg-black text-white';
    }
    
    const colors = {
      restaurant: "bg-blue-500",
      operator: "bg-green-500",
      consolidator: "bg-purple-500",
    };
    return colors[type as keyof typeof colors] || "bg-gray-500";
  };

  if (contacts.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">
            Nenhum contato encontrado.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Layout Mobile/Tablet - Cards
  if (isMobile) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Lista de Contatos</CardTitle>
              {orphanContactIds.size > 0 && (
                <Button
                  variant={showOrphansOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowOrphansOnly(!showOrphansOnly)}
                  className="gap-2"
                >
                  <AlertTriangle className="h-4 w-4" />
                  {showOrphansOnly ? "Todos" : orphanContactIds.size}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {filteredContacts.map((contact) => (
              <Card key={contact.id} className="relative">
                <CardContent className="pt-6 space-y-3">
                  {isOrphan(contact.id) && (
                    <Badge variant="outline" className="absolute top-2 right-2 bg-amber-50 text-amber-700 border-amber-300">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Sem vínculo
                    </Badge>
                  )}
                  
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      <Building2 className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{contact.company}</p>
                        <Badge className={`${getTypeColor(contact.entityType, contact.source)} text-xs mt-1`}>
                          {getTypeLabel(contact.entityType, contact.source)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="font-medium">{`${contact.firstName} ${contact.lastName}`.trim()}</p>
                      {contact.position && (
                        <p className="text-muted-foreground text-xs">{contact.position}</p>
                      )}
                    </div>

                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{contact.email}</span>
                      </a>
                    )}

                    {(contact.cellPhone || contact.phone) && (
                      <div className="space-y-1">
                        {contact.cellPhone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span>{contact.cellPhone}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-muted-foreground text-xs">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {(contact.region || contact.state) && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="text-xs">
                          {contact.region && contact.state 
                            ? `${contact.region} - ${contact.state}`
                            : contact.region || contact.state}
                        </span>
                      </div>
                    )}
                  </div>

                  {canEdit && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(contact)}
                        className="flex-1"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        <EditContactDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          contact={selectedContact}
          onUpdate={handleUpdate}
          onDelete={handleDeleteClick}
        />

        <DeleteRowDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          recordType="contato"
          recordName={`${contactToDelete?.firstName || ""} ${contactToDelete?.lastName || ""}`.trim() || contactToDelete?.company || ""}
        />
      </>
    );
  }

  // Layout Desktop - Tabela Compacta
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lista de Contatos</CardTitle>
            {orphanContactIds.size > 0 && (
              <Button
                variant={showOrphansOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowOrphansOnly(!showOrphansOnly)}
                className="gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                {showOrphansOnly ? "Mostrar todos" : `${orphanContactIds.size} sem vínculo`}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Comunicação</TableHead>
                  <TableHead>Região/Estado</TableHead>
                  {canEdit && <TableHead className="sticky right-0 bg-background text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <Badge className={getTypeColor(contact.entityType, contact.source)}>
                        {getTypeLabel(contact.entityType, contact.source)}
                      </Badge>
                    </TableCell>

                    <TableCell className="font-medium">
                      <div className="space-y-1 min-w-[150px]">
                        <div>{contact.company}</div>
                        {isOrphan(contact.id) && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Sem vínculo
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="min-w-[150px]">
                        <div className="font-medium">
                          {`${contact.firstName} ${contact.lastName}`.trim()}
                        </div>
                        {contact.position && (
                          <div className="text-xs text-muted-foreground">
                            {contact.position}
                          </div>
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1 min-w-[180px]">
                        {contact.email && (
                          <a
                            href={`mailto:${contact.email}`}
                            className="flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{contact.email}</span>
                          </a>
                        )}
                        {contact.cellPhone && (
                          <div className="flex items-center gap-1 text-xs">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            {contact.cellPhone}
                          </div>
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="text-sm min-w-[120px]">
                        {contact.region && contact.state 
                          ? `${contact.region} - ${contact.state}`
                          : contact.region || contact.state || '-'}
                      </div>
                    </TableCell>

                    {canEdit && (
                      <TableCell className="sticky right-0 bg-background text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(contact)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditContactDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        contact={selectedContact}
        onUpdate={handleUpdate}
        onDelete={handleDeleteClick}
      />

      <DeleteRowDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        recordType="contato"
        recordName={`${contactToDelete?.firstName || ""} ${contactToDelete?.lastName || ""}`.trim() || contactToDelete?.company || ""}
      />
    </>
  );
}
